import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { ContactSupportLink } from "@/components/app/ContactSupport";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState("checking"); // checking | paid | expired | error
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/billing/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          await refresh();
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
      } catch {
        // ignore and retry
      }
      if (attempts.current >= 12) {
        setStatus("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, refresh]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-2xl mx-auto text-slate-900">
        <div
          className="rounded-3xl p-10 text-center backdrop-blur-xl border"
          style={{
            background: "linear-gradient(135deg, rgba(139,92,246,0.10), rgba(236,72,153,0.04))",
            borderColor: "rgba(139,92,246,0.25)",
            boxShadow: "0 0 40px rgba(139,92,246,0.15)",
          }}
          data-testid={`billing-result-${status}`}
        >
          {status === "checking" && (
            <>
              <Loader2 className="h-10 w-10 mx-auto mb-4 animate-spin text-sky-600" />
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" style={headingFont}>
                Confirming{" "}
                <span className="text-sky-600" style={accentFont}>payment</span>…
              </h1>
              <p className="text-sm text-slate-600">Hang tight, this usually takes a few seconds.</p>
            </>
          )}
          {status === "paid" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-400" />
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" style={headingFont}>
                You&apos;re on{" "}
                <span className="text-sky-600" style={accentFont}>Pro</span>.
              </h1>
              <p className="text-sm text-slate-600 mb-6">Unlimited screens, scheduling, and groups are now unlocked.</p>
              <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm px-6 font-semibold" onClick={() => navigate("/dashboard")} data-testid="back-to-dashboard">Go to dashboard</Button>
            </>
          )}
          {status === "expired" && (
            <>
              <XCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" style={headingFont}>Session expired</h1>
              <p className="text-sm text-slate-600 mb-6">Please try again from the billing page.</p>
              <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm px-6 font-semibold" onClick={() => navigate("/dashboard/billing")}>Back to billing</Button>
            </>
          )}
          {status === "error" && (
            <>
              <XCircle className="h-12 w-12 mx-auto mb-4 text-red-400" />
              <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mb-2" style={headingFont}>Could not confirm payment</h1>
              <p className="text-sm text-slate-600 mb-6">If you completed the payment, refresh in a minute or <ContactSupportLink category="billing" subject="Payment not confirmed" testId="billing-success-contact-support" className="text-sky-600 hover:text-sky-700 underline underline-offset-2">contact support</ContactSupportLink>.</p>
              <Button className="rounded-full bg-sky-500 hover:bg-sky-600 text-white shadow-sm px-6 font-semibold" onClick={() => navigate("/dashboard/billing")}>Back to billing</Button>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
